<template>
  <div class="validator-management">
    <div class="controls">
      <div class="controls__start experiment-header">
        <div class="d-flex justify-content-between align-items-center">
          <span id="experiment-header-title" class="controls__count">{{ currentExperiment ? currentExperiment.name : '' }}</span>
          <EncodedIdField
            :id="expID"
            :type="'experiment'"
            :hideLabel="true"
            class="ml-2"
          />
        </div>
        <span v-if="loading" class="controls__count">Validate Sessions</span>
        <span v-else class="controls__count">Validate Sessions {{ filteredValidateSessions ? `(${filteredValidateSessions.length})` : '' }}</span>
      </div>
      <div class="controls__middle">
        <HelpTip class="ml-2">
          Validate session list displays both ongoing and previous sessions associated with a specific experiment.
          You can view details, metrics, and logs by selecting any session card.
          To compare multiple validate sessions within the experiment, choose "Compare" from Actions.
        </HelpTip>
      </div>
      <div class="controls__end">
        <div class="controls__actions">
          <FilterButton
            :filter="validationFilters"
            :filter-count="activeFilterCount"
            :active="showFilterPanel"
            @click="showFilterPanel = !showFilterPanel"
            @clear-filters="clearAllFilters"
          >
            <v-icon start>mdi-filter</v-icon>
            Filter
          </FilterButton>
          <button
            v-if="!isPublic && currentUsername"
            type="button"
            class="button-light"
            v-tooltip:bottom="isMySessionOnly ? 'Show Sessions for All Users' : `Show Sessions for ${currentUsername} only`"
            @click="filterToMySessions"
          >
            {{ isMySessionOnly ? 'All Users' : 'My Sessions Only' }}
          </button>
          <ActionsButton
            v-if="permissions && permissions.trainer_write"
            label="Actions"
            :actions="validatorActions"
            :showCount="true"
            :selectedCount="selectedCount"
            @action-clicked="handleValidatorActionClick"
          />
        </div>
      </div>
    </div>

    <div v-if="loading" class="empty-state">
      <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
    </div>

    <template v-else>
      <div v-if="filteredValidateSessions && filteredValidateSessions.length > 0" class="validator-card-container scrollbar">
        <ValidatorCard
          v-for="(s, i) in filteredValidateSessions"
          :key="i"
          :session="s"
          :permissions="permissions"
          :selected="selectedSessions[s.id] || false"
          @show-charts="handleSessionChartsClicked(s)"
          @delete-session="openConfirmDeleteValidation(s)"
          @refresh-session="handleRefreshSession(s)"
          @info-clicked="handleSessionInfoClicked(s)"
          @session-selected="handleSessionSelected"
          @edit-session="handleEditSessionClick(s)"
          @description-changed="handleSessionDescriptionChanged"
        />
      </div>
      <div
        v-else
        class="empty-state"
      >
        <EmptyState
          :title="'No validation sessions found for this experiment'"
          :subtitle="'To add a new validation session, click the New action'"
        />
      </div>
    </template>

    <div
      ref="filterPanelEl"
      :class="['filter-panel', { 'filter-panel--open': showFilterPanel }]"
    >
      <div class="filter-panel__header">
        <h3>Filters</h3>
        <div class="filter-panel__actions">
          <button
            type="button"
            class="get-platforms-btn"
            :disabled="updatingPlatforms"
            @click="handleUpdatePlatforms"
          >
            <template v-if="updatingPlatforms">
              <div class="lds-ring lds-ring--small"><div /><div /><div /><div /></div>
            </template>
            <template v-else>
              <v-icon start size="14">mdi-chip</v-icon>
            </template>
            Get Platforms
          </button>
          <button
            type="button"
            class="clear-filters-btn"
            :disabled="activeFilterCount === 0"
            @click="clearAllFilters"
          >
            Clear All
          </button>
          <button class="filter-panel__close" @click="showFilterPanel = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
      </div>
      <div class="filter-panel__content">
        <ValidationFilters
          v-model="validationFilters"
          :status-list="statusList"
          :users-list="usersList"
          :manage-type-list="manageTypeList"
          :platform-type-list="instanceTypeList"
        />
      </div>
    </div>

    <AppStartModal
      ref="profilerAppModal"
      appName="edgefirst-profiler"
      :defaultValues="profilerAppDefaultValues"
    />
    <ValidationSessionEditDialog
      ref="validationSessionEditDialog"
      :selectedSessions="validateSessions"
      @edit-session="handleEditSessionConfirmed"
    />
    <ConfirmModal
      ref="confirmModal"
      :messageHeader="confirmMessageHeader"
      :message="confirmMessage"
      :buttonClass="'button-delete'"
      :buttonText="buttonText"
      @confirmed="confirmFunction"
    />
    <ProfilingAddValSessionDialog
      ref="profilingAddValSessionDialog"
    />
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import ValidatorCard from '@/components/ValidationPage/ValidatorCardV2.vue';
import ValidateSessionDetailsModal from '@/components/ValidationPage/ValidateSessionDetailsModal.vue';
import ValidationSessionEditDialog from '@/components/ValidationPage/ValidationSessionEditDialog.vue';
import ProfilingAddValSessionDialog from '@/components/ProfilerComponents/ProfilingAddValSessionDialog.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import ActionsButton from '@/components/ActionsButton.vue';
import ValidationFilters from '@/components/ValidationPage/ValidationFilters.vue';
import FilterButton from '@/components/FilterButton.vue';
import AppStartModal from '@/components/Apps/AppStartModal.vue';
import { useToast } from 'vue-toast-notification';
import HelpTip from '@/components/HelpTip.vue';
import EmptyState from '@/components/EmptyState.vue';
import 'vue-toast-notification/dist/theme-sugar.css';
import { decodeID, encodeID } from '@/assets/js/utils.js';

const $toast = useToast();

export default {
  name: "ValidatorManagement",
  components: {
    ConfirmModal,
    ValidatorCard,
    ValidateSessionDetailsModal,
    ValidationSessionEditDialog,
    ProfilingAddValSessionDialog,
    EncodedIdField,
    HelpTip,
    EmptyState,
    ActionsButton,
    ValidationFilters,
    FilterButton,
    AppStartModal,
  },
  inject: {
    isPublic: { default: false },
  },
  props: {
    projectId: {
      type: String,
      default: null,
    },
    expId: {
      type: String,
      default: null,
    },
  },
  emits: [
    'update-list',
  ],
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      expID: null,
      selectedValidateSession: null,
      validateSessions: null,
      interval: null,
      confirmMessage: null,
      confirmMessageHeader: null,
      toDelete: null,
      confirmFunction: null,
      buttonText: 'Delete',
      permissions: null,
      loading: true,
      updatingPlatforms: false,
      selectedSessions: {},
      showFilterPanel: false,
      validationFilters: {
        startDate: '',
        endDate: '',
        showDeleted: false,
        dynamicFilters: {},
      },
      validatorActions: [
        { icon: 'add', label: 'New', eventKey: 'new', tooltip: 'Create New Validation Session' },
        { icon: 'chart_multiple', label: 'Compare', eventKey: 'compare', tooltip: 'Compare validation sessions' },
        { icon: 'edit', label: 'Edit', eventKey: 'edit', tooltip: 'Edit selected validation session' },
        { icon: 'apps', label: 'Add to profiling', eventKey: 'add-to-profiling', tooltip: 'Add selected validation sessions to a profiling session' },
        { icon: 'delete', label: 'Delete', eventKey: 'delete', isAlert: true, tooltip: 'Delete selected validation sessions' },
      ],
    };
  },
  computed: {
    currentUsername() {
      return this.$store.state.user?.user?.username || '';
    },
    isMySessionOnly() {
      const usernames = this.validationFilters.dynamicFilters?.username || [];
      return usernames.length === 1 && usernames[0] === this.currentUsername;
    },
    currentProjectId() {
      if (this.currentExperiment?.project_id) {
        return this.currentExperiment.project_id;
      }

      if (this.currentExperiment?.project?.id) {
        return this.currentExperiment.project.id;
      }

      return Number(this.projectId) || 0;
    },
    profilerAppDefaultValues() {
      return {
        trainer: {
          project_id: this.currentProjectId || null,
          experiment_id: this.currentExperiment?.id || null,
        },
      };
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(exp) {
        this.$store.commit('experiments/setCurrentExperiment', exp);
      },
    },
    currentValidateSession: {
      get() {
        return this.$store.state.experiments.currentValidateSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentValidateSession', session);
      },
    },
    notEmpty() {
      return this.validateSessions && this.validateSessions.length > 0;
    },
    selectedCount() {
      return Object.values(this.selectedSessions).filter(Boolean).length;
    },
    statusList() {
      return [
        { key: 'Queued', value: 'queued' },
        { key: 'Pending', value: 'pending' },
        { key: 'Initializing', value: 'init' },
        { key: 'Running', value: 'running' },
        { key: 'Complete', value: 'complete' },
        { key: 'Terminating', value: 'terminating' },
        { key: 'Terminated', value: 'terminated' },
        { key: 'Error', value: 'error' },
        { key: 'Deleted', value: 'deleted' },
      ];
    },
    manageTypeList() {
      return [
        { key: 'AWS', value: 'aws' },
        { key: 'AWS Batch', value: 'aws_batch' },
        { key: 'User', value: 'user' },
        { key: 'Kubernetes', value: 'kubernetes' },
      ];
    },
    instanceTypeList() {
      const types = new Set();
      (this.validateSessions || []).forEach((session) => {
        const instanceType = String(session?.docker_task?.instance_type || '').trim();
        if (instanceType) {
          types.add(instanceType);
        }
      });
      return Array.from(types).sort((a, b) => a.localeCompare(b)).map((t) => ({ key: t, value: t }));
    },
    usersList() {
      const users = new Set();
      (this.validateSessions || []).forEach((session) => {
        const username = String(session?.docker_task?.username || session?.username || '').trim();
        if (username) {
          users.add(username);
        }
      });
      return Array.from(users).sort((a, b) => a.localeCompare(b)).map((user) => ({ key: user, value: user }));
    },
    activeFilterCount() {
      let count = 0;

      if (this.validationFilters.startDate) count += 1;
      if (this.validationFilters.endDate) count += 1;
      if (this.validationFilters.showDeleted) count += 1;

      Object.values(this.validationFilters.dynamicFilters || {}).forEach((values) => {
        if ((Array.isArray(values) && values.length > 0) || (typeof values === 'string' && values.trim().length > 0)) {
          count += 1;
        }
      });

      return count;
    },
    filteredValidateSessions() {
      const sessions = [...(this.validateSessions || [])];
      const dynamicFilters = this.validationFilters.dynamicFilters || {};
      const selectedStatuses = (dynamicFilters.status || []).map((status) => String(status).toLowerCase());
      const selectedManageTypes = (dynamicFilters.manage_types || []).map((type) => String(type).toLowerCase());
      const selectedPlatformTypes = (dynamicFilters.platform_types || []).map((t) => String(t).toLowerCase());
      const selectedUsers = (dynamicFilters.username || []).map((user) => String(user).toLowerCase());
      const selectedName = String(dynamicFilters.name || '').trim().toLowerCase();
      const startDate = this.validationFilters.startDate ? new Date(this.validationFilters.startDate) : null;
      const endDate = this.validationFilters.endDate ? new Date(this.validationFilters.endDate) : null;

      if (endDate && !Number.isNaN(endDate.getTime())) {
        endDate.setHours(23, 59, 59, 999);
      }

      return sessions
        .filter((session) => {
          if (this.validationFilters.showDeleted) {
            return true;
          }
          const statuses = [
            session?.status,
            session?.status_info?.status,
            session?.docker_task?.status,
          ].filter(Boolean);
          return !statuses.some((status) => String(status).toLowerCase().includes('deleted'));
        })
        .filter((session) => {
          if (selectedStatuses.length === 0) return true;
          const statuses = [
            session?.status,
            session?.status_info?.status,
            session?.docker_task?.status,
          ].filter(Boolean).map((status) => String(status).toLowerCase());
          return selectedStatuses.some((selectedStatus) => statuses.some((status) => status.includes(selectedStatus)));
        })
        .filter((session) => {
          if (selectedManageTypes.length === 0) return true;
          const manageType = String(session?.docker_task?.manage_type || session?.manage_type || '').toLowerCase();
          return selectedManageTypes.includes(manageType);
        })
        .filter((session) => {
          if (selectedPlatformTypes.length === 0) return true;
          const instanceType = String(session?.docker_task?.instance_type || '').toLowerCase();
          return selectedPlatformTypes.includes(instanceType);
        })
        .filter((session) => {
          if (selectedUsers.length === 0) return true;
          const username = String(session?.docker_task?.username || session?.username || '').toLowerCase();
          return selectedUsers.includes(username);
        })
        .filter((session) => {
          if (!selectedName) return true;
          const sessionName = String(session?.docker_task?.name || '').toLowerCase();
          return sessionName.includes(selectedName);
        })
        .filter((session) => {
          if (!startDate && !endDate) return true;

          const rawDate = session?.docker_task?.date;

          if (!rawDate) return false;

          const createdAt = new Date(rawDate);
          if (Number.isNaN(createdAt.getTime())) return false;
          if (startDate && createdAt < startDate) return false;
          if (endDate && createdAt > endDate) return false;
          return true;
        });
    },
  },
  watch: {
    async currentExperiment(exp) {
      this.getValidateSessions(exp.id);
    },
  },
  beforeUnmount() {
    this.stopInterval();
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
  },
  async mounted() {
    // Get expId from route params and decode it if needed
    const expIdParam = this.$route.params.expId;
    const decimalExpId = expIdParam ? decodeID(expIdParam) || parseInt(expIdParam, 10) : null;
    this.expID = decimalExpId;
    
    if (!decimalExpId && this.currentExperiment) {
      this.$router.replace({ name: `${this.isPublic ? 'public.' : ''}validations.list`, params: { expId: encodeID(this.currentExperiment.id, 'exp') } });
    }

    if (this.currentExperiment) {
      this.getValidateSessions(this.currentExperiment.id);
    }

    await this.getProjectAccessForSingleUser();

    this.startInterval();
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
  },
  methods: {
    handleDocumentMouseDown(event) {
      if (!this.showFilterPanel) return;
      const panel = this.$refs.filterPanelEl;
      if (panel && panel.contains(event.target)) return;
      const target = event.target;
      if (target.closest && (target.closest('.button-light') || target.closest('.popper-content') || target.closest('.v-overlay-container') || target.closest('.v-menu'))) {
        return;
      }
      this.showFilterPanel = false;
    },
    clearAllFilters() {
      const currentDynamic = this.validationFilters.dynamicFilters || {};
      const cleared = {};
      Object.keys(currentDynamic).forEach((key) => {
        const current = currentDynamic[key];
        if (Array.isArray(current)) {
          cleared[key] = [];
        } else if (typeof current === 'string') {
          cleared[key] = '';
        } else {
          cleared[key] = null;
        }
      });

      this.validationFilters = {
        startDate: '',
        endDate: '',
        showDeleted: false,
        dynamicFilters: cleared,
      };
    },
    filterToMySessions() {
      const username = this.isMySessionOnly ? [] : [this.currentUsername];
      this.validationFilters = {
        ...this.validationFilters,
        dynamicFilters: {
          ...(this.validationFilters.dynamicFilters || {}),
          username,
        },
      };
    },
    handleCompareSessionsClicked() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));

      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select at least one validation session',
          type: 'warning',
          position: 'top',
        });
        return;
      }

      this.handleOpenCompareSessionsPage(selectedIds);
    },
    handleValidatorActionClick(eventKey) {
      if (eventKey === 'new') {
        this.$refs.profilerAppModal?.showModal();
      } else if (eventKey === 'compare') {
        this.handleCompareSessionsClicked();
      } else if (eventKey === 'edit') {
        this.handleEditSelectedSessions();
      } else if (eventKey === 'add-to-profiling') {
        this.handleAddSelectedSessionsToProfiling();
      } else if (eventKey === 'delete') {
        this.handleDeleteSelectedSessions();
      }
    },
    async handleUpdatePlatforms() {
      if (!this.currentExperiment || !this.currentExperiment.id) {
        return;
      }
      this.updatingPlatforms = true;
      const resp = await this.dataConnect.getValidateSessionListByExperiment({ experiment_id: this.currentExperiment.id, reverse: true, short: false })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to update platforms: ${error}`,
            type: 'error',
            position: 'top',
          });
        });
      this.updatingPlatforms = false;
      if (!resp || resp.error || !resp.result) {
        return;
      }
      const updatedSessions = resp.result;
      (updatedSessions || []).forEach((updated) => {
        const index = (this.validateSessions || []).findIndex((s) => s.id === updated.id);
        if (index >= 0) {
          Object.assign(this.validateSessions[index], updated);
        }
      });
    },
    handleAddSelectedSessionsToProfiling() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));

      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select at least one validation session',
          type: 'warning',
          position: 'top',
        });
        return;
      }

      const selectedValidationSessions = (this.validateSessions || [])
        .filter((session) => selectedIds.includes(session.id));

      this.$refs.profilingAddValSessionDialog?.showModal(selectedValidationSessions);
    },
    handleSessionSelected({ sessionId, isSelected }) {
      this.selectedSessions[sessionId] = isSelected;
      this.$forceUpdate();
    },
    handleDeleteSelectedSessions() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select at least one session to delete',
          type: "warning",
          position: "top",
        });
        return;
      }
      this.confirmMessage = `Are you sure you want to move ${selectedIds.length} validation session(s) to recycle bin?`;
      this.confirmMessageHeader = 'Remove Sessions';
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.handleDeleteValidateSessions(selectedIds);
        this.selectedSessions = {};
      };
    },
    handleEditSelectedSessions() {
      const selectedIds = Object.keys(this.selectedSessions)
        .filter((id) => this.selectedSessions[id])
        .map((id) => parseInt(id, 10));
      if (selectedIds.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select a validation session to edit',
          type: "warning",
          position: "top",
        });
        return;
      }
      if (selectedIds.length > 1) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select only one session to edit',
          type: "warning",
          position: "top",
        });
        return;
      }
      const selectedSession = this.validateSessions.find((s) => s.id === selectedIds[0]);
      if (selectedSession) {
        this.$refs.validationSessionEditDialog.openModal(selectedSession);
      }
    },
    handleEditSessionConfirmed(editedSession) {
      const index = this.validateSessions.findIndex((s) => s.id === editedSession.id);
      if (index > -1) {
        this.validateSessions[index] = { 
          ...this.validateSessions[index], 
          ...editedSession,
          docker_task: {
            ...this.validateSessions[index].docker_task,
            name: editedSession.name,
          }
        };
      }
      this.selectedSessions = {};
    },
    async handleSessionDescriptionChanged(payload) {
      const session = payload?.session;
      if (!session?.id) {
        return;
      }

      const params = {
        validate_session_id: session.id,
        name: session.docker_task?.name || session.name || '',
        description: payload?.description || '',
      };

      await this.dataConnect.updateValidationSession(params);

      const index = this.validateSessions.findIndex((s) => s.id === session.id);
      if (index > -1) {
        this.validateSessions[index].description = payload?.description || '';
      }
    },
    async getValidateSessions(id) {
      if (!this.validateSessions) {
        this.loading = true;
      }
      const newSessions = await this.getValidateSessionList(id);
      
      if (!this.validateSessions) {
        this.validateSessions = newSessions;
      } else {
        // Update existing sessions and add new ones without replacing the array
        const existingIds = new Set(this.validateSessions.map(s => s.id));
        const newIds = new Set(newSessions.map(s => s.id));
        
        // Update or add sessions
        for (const newSession of newSessions) {
          const existingIndex = this.validateSessions.findIndex(s => s.id === newSession.id);
          if (existingIndex >= 0) {
            // Update existing session properties
            Object.assign(this.validateSessions[existingIndex], newSession);
          } else {
            // Add new session
            this.validateSessions.unshift(newSession);
          }
        }
        
        // Remove sessions that no longer exist
        for (let i = this.validateSessions.length - 1; i >= 0; i--) {
          if (!newIds.has(this.validateSessions[i].id)) {
            this.validateSessions.splice(i, 1);
          }
        }
      }
      
      this.loading = false;
    },
    async handleRefreshSession(session) {
      const index = this.validateSessions.findIndex((e) => e.id === session.id);
      if (index > -1) {
        this.validateSessions[index] = await this.getSession(session.id);
      }
    },
    async getProjectAccessForSingleUser() {
      if (this.currentProjectId && this.$store.state.user?.user?.username) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentProjectId,
          username: this.$store.state.user.user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            } else if (!data || data.error) {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Failed to retrieve project access: ${data.error.message}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((e) => {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to retrieve project access: ${e}`,
              type: "error",
              position: "top",
            });
          });
      }
    },
    stopInterval() {
      clearInterval(this.interval);
      this.interval = null;
    },
    startInterval() {
      this.interval = setInterval(async () => {
        await this.getValidateSessions();
      }, 30000);
    },
    async getValidateSessionList() {
      if (!this.currentExperiment || !this.currentExperiment.id) {
        return [];
      }
      const resp = await this.dataConnect.getValidateSessionListByExperiment({ experiment_id: this.currentExperiment.id, reverse: true, short: true })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve validate sessions: ${error}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve validate sessions: ${resp.error}`,
          type: "error",
          position: "top",
        });
        return [];
      }
      if (!resp || resp.error || !resp.result) {
        return [];
      }
      return resp.result;
    },
    async getSession(id) {
      const resp = await this.dataConnect.getValidateSession({
        validate_session_id: id,
      })
        .catch((e) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to retrieve validate session: ${e}`,
            type: "error",
            position: "top",
          });
        });
      if (!resp || resp.error || !resp.result) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Failed to retrieve validate session: ${resp.error}`,
          type: "error",
          position: "top",
        });
        return null;
      }
      return resp.result;
    },
    openConfirmDeleteValidation(session) {
      this.confirmMessage = `Are you sure you want to move this validate session to recycle bin?`;
      this.confirmMessageHeader = 'Remove Session';
      this.toDelete = session;
      this.buttonText = 'Remove';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.handleDeleteValidateSessions([session.id]);
      };
    },
    async handleDeleteValidateSessions(ids) {
      await this.dataConnect.deleteValidateSessions({
        session_ids: ids,
      })
        .then(async (data) => {
          if (!data || data.error) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to delete validate session: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
          await this.getValidateSessions();
        })

        .catch((error) => {
          if (error?.error?.code === 403) return;
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to delete validate session: ${error?.error?.message || error?.message || 'Unexpected error'}`,
            type: "error",
            position: "top",
          });
        });

      this.toDelete = null;
    },
    async handleGetExperiment(id) {
      await this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (data.result) {
            this.currentExperiment = data.result;
          } else if (data.error) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiment: ${data.error.message}`,
              type: "error",
              position: "top",
            });
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleSessionInfoClicked(session) {
      this.currentValidateSession = session;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.details`, params: { sessionId: encodeID(session.id, 'v') } });
    },
    handleEditSessionClick(session) {
      this.$refs.validationSessionEditDialog.openModal(session);
    },
    handleSessionChartsClicked(session) {
      this.currentValidateSession = session;
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}experiment.validation.details`, params: { sessionId: encodeID(session.id, 'v') }, query: { mode: 'charts' } });
    },
    handleOpenCompareSessionsPage(sessionIds) {
      this.$router.push({ name: `${this.isPublic ? 'public.' : ''}validation.compare`, params: { expId: encodeID(this.currentExperiment.id, 'exp') }, query: { session_ids: encodeURI(JSON.stringify(sessionIds)) } });
    },
  },
};
</script>

<style lang="scss" scoped>
.validator-management {
  position: relative;
}

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;

    &.experiment-header {
      flex-direction: column;
    }
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    width: fit-content
  }

  &__middle {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}
@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.validator-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  background: #e53935;
  color: #ffffff;
}

.filter-panel {
  position: fixed;
  right: 0;
  top: 0;
  height: 100%;
  width: 0;
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease-in-out;
  overflow-y: auto;
  z-index: 1300;
  display: flex;
  flex-direction: column;

  &--open {
    width: 380px;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;

    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }

  &__content {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  }
}

.clear-filters-btn {
  padding: 5px 12px;
  height: 30px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #555;
  border-radius: 4px;
  background: transparent;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(0,0,0,0.06);
  }

  &:active {
    opacity: 0.8;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.validator-card-container {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  height: calc(100vh - 180px);
  align-content: flex-start;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}

.updating-platforms-indicator {
  display: none;
}

.get-platforms-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  height: 30px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid #555;
  background: transparent;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0,0,0,0.06);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.lds-ring--small {
  width: 36px;
  height: 36px;

  div {
    width: 28px;
    height: 28px;
    margin: 4px;
    border-width: 3px;
  }
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
